/**
 * This library parses a filter string into tokens, expressions, terms, or an abstract syntax tree. Note that it does
 * not currently validate whether the filter is a valid query. We currently leave that to the back end.
 */

import * as K from 'common/constants';

import { ANY_TYPE, Expression, EXPRESSION_TYPE, MaybeNode, Node, PARSE_TYPE, Term, Token, TOKEN_TYPE } from './types';

import { AGGREGATE_TOKEN_TYPE, ONE_CHAR, REGEXES, TIME_FUNCTIONS, TWO_CHARS, WORDS } from './constants';

/**
 * Find the rest of an opened string. If the string isn't closed, return what we found up to the end.
 *
 * @param filter        filter text
 * @param pos           starting position within filter
 * @param quoteChar     opening quote character that was found
 * @param tokens        list of tokens found so far
 *
 * @returns token with start, end, text, type, and closed properties
 */
export const getStringToken = (filter: string, pos: number, quoteChar: string, tokens: Array<Token>): Token => {
  const token: Token = {} as Token;
  token.parseType = PARSE_TYPE.TOKEN;
  token.type = TOKEN_TYPE.STRING;
  token.start = pos;
  let p = pos;
  let closed = false;
  p += 1;
  while (p < filter.length) {
    const c = filter[p];
    p += 1;
    if (c === '\\') {
      p = p === filter.length ? p : p + 1;
    } else if (c === quoteChar) {
      closed = true;
      break;
    }
  }
  token.end = p;
  token.text = filter.substring(token.start, token.end);
  const prevPrevToken = tokens.length >= 2 && tokens[tokens.length - 2];
  if (closed && prevPrevToken && prevPrevToken.type === TOKEN_TYPE.TIME_FUNCTION) {
    token.type = TOKEN_TYPE.TIME_ZONE;
  }

  token.complete = closed;

  return token;
};

/**
 * Get the next token from the filter text.
 *
 * @param filter    filter text
 * @param pos       start position within filter text
 * @param tokens    list of tokens found so far
 *
 * @returns a token with start, end, text, and type properties, and possibly subtype
 */
export const getToken = (filter: string, pos: number, tokens: Array<Token>): Token => {
  let type = TOKEN_TYPE.UNKNOWN;
  const sub = filter.substring(pos);
  const firstTwo = filter.substring(pos, pos + 2);
  const first = filter.substring(pos, pos + 1);
  const prevToken: Token | null = tokens.length >= 1 ? tokens[tokens.length - 1] : null;
  const prevPrevToken: Token | null = tokens.length >= 2 ? tokens[tokens.length - 2] : null;
  const prevTokenType = prevToken?.type;
  let start = pos;
  let match;
  let text = '';
  let i;
  let entry;

  // Try regexes first
  for (i = 0; i < REGEXES.length; i += 1) {
    entry = REGEXES[i];
    match = sub.match(entry.regex);
    if (match) {
      type = entry.type;
      text = match[1];
      break;
    }
  }

  // handle 'contains:matchcase' and 'matches:matchcase', which look like field names (which allow colons)
  if (type === TOKEN_TYPE.WORD && text.indexOf(':') !== -1) {
    if (first === ':' && prevTokenType === TOKEN_TYPE.COMPARISON) {
      text = first;
      type = TOKEN_TYPE.COLON;
    } else {
      const parts = text.split(':');
      if (parts[0] === 'contains' || parts[0] === 'matches') {
        text = parts[0];
        type = TOKEN_TYPE.COMPARISON;
      }
    }
  }

  // If we found a word, try to determine its type
  if (type === TOKEN_TYPE.WORD) {
    const types = Object.values(TOKEN_TYPE);
    for (i = 0; i < types.length; i += 1) {
      entry = WORDS[types[i]];
      if (entry.includes(text)) {
        type = types[i];
        break;
      }
    }
  }

  // Check for a two-character token
  if (type === TOKEN_TYPE.UNKNOWN) {
    for (i = 0; i < TWO_CHARS.length; i += 1) {
      entry = TWO_CHARS[i];
      if (entry.list.includes(firstTwo)) {
        type = entry.type;
        text = firstTwo;
        break;
      }
    }
  }

  // Check for a one-character token
  if (type === TOKEN_TYPE.UNKNOWN) {
    for (i = 0; i < ONE_CHAR.length; i += 1) {
      entry = ONE_CHAR[i];
      if (entry.list.includes(first)) {
        type = entry.type;
        text = first;
        break;
      }
    }
  }

  // A colon after a function indicates an interval
  if ((type === TOKEN_TYPE.UNKNOWN || type === TOKEN_TYPE.WORD) && first === ':') {
    match = sub.match(/^:(\d+\s*\w+)/);
    if (prevTokenType === TOKEN_TYPE.FUNCTION && match) {
      type = TOKEN_TYPE.INTERVAL;
      start += 1; // account for the colon, which isn't part of the matched text
      text = match[1];
    }
  } else if (type === TOKEN_TYPE.WORD && prevTokenType === TOKEN_TYPE.TIMESHIFT) {
    type = TOKEN_TYPE.INTERVAL;
  } else if (type === TOKEN_TYPE.UNKNOWN && first === '$') {
    // A dollar sign can indicate a regex or a system field
    if (firstTwo === '$"') {
      const stringToken = getStringToken(filter, pos + 1, '"', tokens);
      text = '$' + stringToken.text;
      type = TOKEN_TYPE.REGEX;
    }
  } else if (type === TOKEN_TYPE.UNKNOWN && first === '|') {
    type = TOKEN_TYPE.PIPE;
    text = '|';
  }

  const prevFirst = prevToken?.text?.substring(0, 1) || '';

  // A word followed by a comparison operator is a field (facet)
  if (
    type === TOKEN_TYPE.COMPARISON &&
    prevToken &&
    prevTokenType === TOKEN_TYPE.WORD &&
    /[$a-zA-Z:]/.test(prevFirst)
  ) {
    prevToken.type = TOKEN_TYPE.FIELD;
  }

  // A word followed by 'timeshift' (in function argument) is a field
  if (type === TOKEN_TYPE.WORD && text === TOKEN_TYPE.TIMESHIFT && prevToken) {
    prevToken.type = TOKEN_TYPE.FIELD;
    type = TOKEN_TYPE.TIMESHIFT;
  }

  // A word followed by 'where' (in function argument) is a field; 'where' follows two tokens after left paren or 'timeshift'
  if (
    type === TOKEN_TYPE.WORD &&
    text === TOKEN_TYPE.WHERE &&
    prevToken &&
    [TOKEN_TYPE.LEFT_PAREN, TOKEN_TYPE.TIMESHIFT].includes(prevPrevToken?.type as TOKEN_TYPE)
  ) {
    prevToken.type = TOKEN_TYPE.FIELD;
    type = TOKEN_TYPE.WHERE;
  }

  // A bare asterisk is a wildcard right-side value, doesn't need quotes
  if (type === TOKEN_TYPE.MATH_OPERATOR && text === '*' && prevTokenType === TOKEN_TYPE.COMPARISON) {
    type = TOKEN_TYPE.STRING;
  }

  // Pull out time-related functions
  if (type === TOKEN_TYPE.FUNCTION && TIME_FUNCTIONS.includes(text)) {
    type = TOKEN_TYPE.TIME_FUNCTION;
  }

  // figure out if a - is performing subtraction (rather than being part of a negative number)
  if (
    type === TOKEN_TYPE.NUMBER &&
    first === '-' &&
    prevTokenType &&
    [TOKEN_TYPE.NUMBER, TOKEN_TYPE.RIGHT_PAREN].includes(prevTokenType as TOKEN_TYPE)
  ) {
    type = TOKEN_TYPE.MATH_OPERATOR;
    text = '-';
  }

  // we don't know what this is; let's consider it to be an unquoted bare string up to the first space
  if (type === TOKEN_TYPE.UNKNOWN) {
    type = TOKEN_TYPE.STRING;
    match = sub.match(/^([^ \t]+)\s*/);
    if (match) {
      text = match[1];
    }
  }

  const end = text ? start + text.length : start + 1;

  return {
    parseType: PARSE_TYPE.TOKEN,
    complete: false,
    start,
    end,
    text,
    type,
  };
};

/**
 * Break the given filter up into a list of tokens. A token is an atomic unit of semantic meaning. Types of tokens
 * include: field, system field, function, number, comparison operator, etc. Text for which a type cannot be
 * determined is ignored (does not create a token).
 *
 * @param filter    a filter string
 *
 * @returns list of tokens, each with a type and possibly other fields (see getToken)
 */
export const tokenizeFilter = (filter = ''): Array<Token> => {
  const tokens = [];
  let token: Token | undefined;
  let i = 0;
  while (i < filter.length) {
    const c = filter[i];
    // if we see a quote, go ahead and get the string
    if (c === "'" || c === '"') {
      token = getStringToken(filter, i, c, tokens);
      i = token.end;
      tokens.push(token);
    } else if (c === ' ' || c === '\n') {
      i += 1;
      if (token) {
        token.complete = true;
      }
    } else if (c === '(' || c === ')') {
      token = {
        parseType: PARSE_TYPE.TOKEN,
        type: c === '(' ? TOKEN_TYPE.LEFT_PAREN : TOKEN_TYPE.RIGHT_PAREN,
        text: c,
        start: i,
        end: i + 1,
      };
      tokens.push(token);
      i += 1;
    } else if (c === '/' && filter[i + 1] === '/') {
      let p = i + 2;
      while (p < filter.length && filter[p] !== '\n') {
        p += 1;
      }
      i = p;
    } else {
      // parse the input for the next token
      token = getToken(filter, i, tokens);
      i = token.end;
      if (token.type !== TOKEN_TYPE.UNKNOWN) {
        if (tokens.length > 0) {
          tokens[tokens.length - 1].complete = true;
        }
        tokens.push(token);
      }
    }
  }

  // console.log('*** ' + tokens.map((t) => (t.parseType === PARSE_TYPE.TOKEN ? t.type : t.parseType)).join(' '));
  return tokens;
};

/**
 * Return the string representation of the given token.
 *
 * @param token    a token
 *
 * @return string representation of the token
 */
export const stringify = (token: Token | null | undefined): string => {
  if (!token) {
    return '';
  }

  if (token.parseType === PARSE_TYPE.TERM) {
    const term: Term = token as Term;
    return term.type === TOKEN_TYPE.STRING ? term.value : [term.field, term.operator, term.value].join(' ');
  }

  if (token.parseType === PARSE_TYPE.EXPRESSION) {
    const expression: Expression = token as Expression;
    const joinChar = expression.type === EXPRESSION_TYPE.LIST ? '' : ' ';
    return '(' + expression.tokens.map((t) => t.text).join(joinChar) + ')';
  }

  if (token.type === TOKEN_TYPE.AND) {
    return '&&';
  }
  if (token.type === TOKEN_TYPE.OR) {
    return '||';
  }
  if (token.type === TOKEN_TYPE.NOT) {
    return '!';
  }

  return token.text;
};

/**
 * Return the string representation of a list of tokens.
 *
 * @param tokens    a list of tokens
 *
 * @return string representation of the list of tokens
 */
export const serializeTokens = (tokens: Token[]): string => tokens.map((token) => stringify(token)).join(' ');

/**
 * Return a string showing the types of the tokens in the given list.
 *
 * @param tokens    a list of tokens
 *
 * @return string list of token types
 */
export const serializeTokenTypes = (tokens: Token[]): string =>
  tokens.map((token) => (token.parseType === PARSE_TYPE.TOKEN ? token.type : token.parseType)).join(' ');

/**
 * Get the first token of the given type in the filter.
 *
 * @param filter    a filter string
 * @param type      token type
 * @returns token of the given type, or null if none found
 */
export const getTokenByType = (filter: string, type: TOKEN_TYPE): Token | null => {
  const tokens = tokenizeFilter(filter);
  return tokens.find((token) => token.type === type) || null;
};

/**
 * Searches from the end of the token list backwards for first token that satisfies the given function.
 *
 * @param tokens    a list of tokens
 * @param func      function that takes a token and performs a test on it
 *
 * @returns the index of the last token that satisfies the given function
 */
export const findIndexOfLastToken = (tokens: Token[], func: (token: Token) => boolean): number => {
  let i = tokens.length - 1;
  for (; i >= 0; i -= 1) {
    if (func(tokens[i])) {
      break;
    }
  }
  return i;
};

/**
 * This function gathers together multi-token expressions into single tokens. An expression is a Scalyr function, a
 * numeric expression composed of a pair of tokens separated by a mathematical operator, or a list of values
 * in parentheses following the operator 'in'. The list of tokens being processed represent
 * a valid filter, so there are some assumptions we can make. The following are expressions:
 *
 *     1024 * 1024
 *     mean:5m(value where $serverHost='frontend-1')
 *     ('aaa', 'bbb', 3)
 *
 * Each of those sequences will produce a single complex token of type 'expression'.
 *
 * @param expandedTokens    list of filter tokens without expressions collapsed
 *
 * TODO: handle function which has internal parens, eg: count(value where ('a' || 'b'))
 *
 * @returns list of filter tokens with expressions collapsed
 */
export const findExpressions = (expandedTokens: Array<Token>): Array<Token> => {
  const collapseExpressions = (tokens: Array<Token>): Array<Token> => {
    const newTokens = [];
    let expTokens;
    for (let i = 0; i < tokens.length; i += 1) {
      const token = tokens[i];
      if (token.parseType === PARSE_TYPE.EXPRESSION) {
        newTokens.push(token);
      } else if (token.type === TOKEN_TYPE.FUNCTION || token.type === TOKEN_TYPE.TIME_FUNCTION) {
        // gather the tokens that belong to the function
        expTokens = [token];
        i += 1; // get next token; it must be a left paren or an interval (which will be followed by a left paren)
        if (tokens[i] && tokens[i].type === TOKEN_TYPE.INTERVAL) {
          expTokens.push(tokens[i]);
          i += 1;
        }
        if (tokens[i] && tokens[i].type === TOKEN_TYPE.LEFT_PAREN) {
          // scan until we find closing right paren (there may be pairs of parens inside)
          let done = false;
          let leftParens = 0;
          while (i < tokens.length && !done) {
            const t = tokens[i];
            if (t.type === TOKEN_TYPE.LEFT_PAREN) {
              leftParens += 1;
            } else if (t.type === TOKEN_TYPE.RIGHT_PAREN) {
              leftParens -= 1;
              if (leftParens === 0) {
                done = true;
              }
            }
            expTokens.push(t);
            // don't increment if we found closing right paren; for loop will handle next increment
            if (!done) {
              i += 1;
            }
          }
        }
        const newToken: Expression = {
          parseType: PARSE_TYPE.EXPRESSION,
          type: TOKEN_TYPE.FUNCTION,
          start: expTokens[0].start,
          end: expTokens[expTokens.length - 1].end,
          text: serializeTokens(expTokens),
          tokens: expTokens,
        };
        newTokens.push(newToken);
      } else if (token.type === TOKEN_TYPE.NUMBER) {
        // gather tokens that have a math operator between them
        const one = tokens[i + 1];
        const two = tokens[i + 2];
        if (
          one &&
          one.type === TOKEN_TYPE.MATH_OPERATOR &&
          two &&
          (two.type === TOKEN_TYPE.NUMBER || two.parseType === PARSE_TYPE.EXPRESSION)
        ) {
          expTokens = tokens.slice(i, i + 3);
          const newToken: Expression = {
            parseType: PARSE_TYPE.EXPRESSION,
            type: EXPRESSION_TYPE.MATH,
            start: token.start,
            end: two.end,
            text: serializeTokens(expTokens),
            tokens: expTokens,
          };
          newTokens.push(newToken);
          i += 2;
        } else {
          newTokens.push(token);
        }
      } else if (token.type === TOKEN_TYPE.COMPARISON && token.text === 'in') {
        newTokens.push(token);
        i += 1; // get next token; it must be a left paren
        if (tokens[i]) {
          if (tokens[i].type === TOKEN_TYPE.LEFT_PAREN) {
            i += 1; // don't include parens in expression
            expTokens = [];
            while (tokens[i] && tokens[i].type !== TOKEN_TYPE.RIGHT_PAREN) {
              expTokens.push(tokens[i]);
              i += 1;
            }
            if (expTokens.length > 0) {
              const newToken: Expression = {
                parseType: PARSE_TYPE.EXPRESSION,
                type: EXPRESSION_TYPE.LIST,
                start: expTokens[0].start,
                end: expTokens[expTokens.length - 1].end,
                text: serializeTokens(expTokens),
                tokens: expTokens,
              };
              newTokens.push(newToken);
            }
          } else if (tokens[i].parseType === PARSE_TYPE.EXPRESSION) {
            newTokens.push(tokens[i]);
          }
        }
      } else {
        newTokens.push(token);
      }
    }

    return newTokens;
  };

  // Make multiple passes through the tokens until no more expressions are found.
  let currentTokens = [...expandedTokens];
  let done = false;
  do {
    const newTokens = collapseExpressions(currentTokens);
    done = newTokens.length === currentTokens.length;
    currentTokens = newTokens;
  } while (!done);

  return currentTokens;
};

// Returns true if the given token is any sort of function
export const isFunction = (type: ANY_TYPE): boolean =>
  type === TOKEN_TYPE.FUNCTION ||
  type === TOKEN_TYPE.PQ_GROUP_FUNCTION ||
  AGGREGATE_TOKEN_TYPE[TOKEN_TYPE.PQ_EXPRESSION_FUNCTION].includes(type as TOKEN_TYPE);

/**
 * Tokenize a filter and collapse certain sequences of tokens into terms. The first step is to collapse tokens into
 * expressions. A term is generally a test that results in true or false, most commonly by comparing one value to another.
 * Token type sequences that form terms are:
 *
 *     {field,system} comparison {string,number,expression,regex,boolean}
 *     {string,word,regex} (implied comparison with $message)
 *
 * @param filter    a filter
 *
 * @returns list of terms, boolean operators (AND, OR, NOT), and parentheses.
 */
export const findTerms = (filter: string): Array<Token> => {
  const tokens = findExpressions(tokenizeFilter(filter));

  // look through list of tokens for sequences that form terms
  const newTokens = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    const prevToken = i > 0 ? tokens[i - 1] : null;
    // look for a left-side token
    if (token.type === TOKEN_TYPE.FIELD) {
      const one = tokens[i + 1];
      const two = tokens[i + 2];
      // see if it's followed by a comparator and a right-side token or expression
      if (
        one &&
        one.type === TOKEN_TYPE.COMPARISON &&
        two &&
        ([TOKEN_TYPE.NUMBER, TOKEN_TYPE.STRING, TOKEN_TYPE.REGEX, TOKEN_TYPE.BOOLEAN].includes(
          two.type as TOKEN_TYPE,
        ) ||
          two.parseType === PARSE_TYPE.EXPRESSION)
      ) {
        const term: Term = {
          parseType: PARSE_TYPE.TERM,
          type: token.type, // term takes type of its left-side value
          start: token.start,
          end: two.end,
          text: '',
          tokens: tokens.slice(i, i + 3),
          field: stringify(token),
          operator: stringify(one),
          value: stringify(two),
        };
        newTokens.push(term);
        i += 2;
      } else {
        newTokens.push({ ...token });
      }
    } else if (
      (token.type === TOKEN_TYPE.STRING || token.type === TOKEN_TYPE.WORD || token.type === TOKEN_TYPE.REGEX) &&
      (!prevToken || prevToken.type !== TOKEN_TYPE.COMPARISON)
    ) {
      // a string or word or regex by itself implies comparison to $message
      const term: Term = {
        parseType: PARSE_TYPE.TERM,
        type: TOKEN_TYPE.STRING, // term takes type of its left-side value
        start: token.start,
        end: token.end,
        text: token.text,
        tokens: [token],
        field: K.FIELD.MESSAGE,
        operator: token.type === TOKEN_TYPE.REGEX ? 'matches' : 'contains',
        value: token.text,
      };
      newTokens.push(term);
    } else {
      // this token should be a boolean operator, function, or a parenthesis
      newTokens.push({ ...token });
    }
  }

  // adjacent terms have an implied AND between them; make that explicit
  const newTerms = [];
  for (let i = 0; i < newTokens.length; i += 1) {
    const token = newTokens[i];
    const { type, parseType } = token;
    const prevToken: Token = i > 0 ? newTokens[i - 1] : ({} as Token);
    const prevType = prevToken.type;
    const prevTokenType = prevToken.parseType;
    if (
      (parseType === PARSE_TYPE.TERM || type === TOKEN_TYPE.LEFT_PAREN || type === TOKEN_TYPE.NOT) &&
      (prevTokenType === PARSE_TYPE.TERM || prevType === TOKEN_TYPE.RIGHT_PAREN)
    ) {
      const andTerm: Term = {} as Term;
      andTerm.parseType = PARSE_TYPE.TOKEN;
      andTerm.type = TOKEN_TYPE.AND;
      newTerms.push(andTerm);
    }
    newTerms.push(token);
  }

  return newTerms;
};

/**
 * Simple recursive descent parser that converts a boolean query into an abstract syntax tree (AST).
 *
 * @param filter    a filter
 *
 * @returns parent node of an abstract syntax tree
 */
export const parse = (filter: string): MaybeNode => {
  const tokens = findTerms(filter);

  // current token - could be a term or a boolean operator
  let token: Token;
  let pos = 0;

  const createNode = (type: ANY_TYPE, nodeToken: Token): Node => ({
    type: type || TOKEN_TYPE.UNKNOWN,
    token: nodeToken,
    parent: null,
    child: null,
    left: null,
    right: null,
    REMOVE: false,
  });

  // current node
  let node: MaybeNode = null;

  // a factor is a term, a negated term, or a parenthesized expression
  const factor = (): void => {
    token = tokens[pos];
    pos += 1;
    if (token && token.parseType === PARSE_TYPE.TERM) {
      node = createNode(PARSE_TYPE.TERM, token);
      token = tokens[pos];
      pos += 1;
    } else if (token && token.type === TOKEN_TYPE.NOT) {
      // NOT has high precedence
      const not = createNode(TOKEN_TYPE.NOT, token);
      factor();
      not.child = node;
      if (node) {
        node.parent = not;
      }
      node = not;
    } else if (token && token.type === TOKEN_TYPE.LEFT_PAREN) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      orTerms();
      token = tokens[pos];
      pos += 1;
    }
  };

  // AND has middle precedence
  const andTerms = (): void => {
    factor();
    while (token && token.type === TOKEN_TYPE.AND) {
      const and = createNode(TOKEN_TYPE.AND, token);
      and.left = node;
      if (node) {
        node.parent = and;
      }
      factor();
      and.right = node;
      if (node) {
        node.parent = and;
      }
      node = and;
    }
  };

  // OR has lowest precedence
  const orTerms = (): void => {
    andTerms();
    while (token && token.type === TOKEN_TYPE.OR) {
      const or = createNode(TOKEN_TYPE.OR, token);
      or.left = node;
      if (node) {
        node.parent = or;
      }
      andTerms();
      or.right = node;
      if (node) {
        node.parent = or;
      }
      node = or;
    }
  };

  orTerms();

  return node; // root node
};

/**
 * Return a string that represents the given AST.
 *
 * @param ast        parent node of an abstract syntax tree that represents a boolean expression
 * @param omitAnd    if true (default), returned string uses implicit AND to join terms
 *
 * @returns string representation of the boolean expression
 */
export const serialize = (ast: MaybeNode, omitAnd = true): string => {
  if (!ast) {
    return '';
  }

  let output = '';

  // inner recursize function (depth-first, pre-order) that returns text for a node and then its children
  const serializeNode = (node: Node): void => {
    if (!node) {
      return;
    }

    const needParens = [TOKEN_TYPE.OR, TOKEN_TYPE.NOT].includes(node.type as TOKEN_TYPE);

    // node is AND or OR
    if (node.left) {
      output += needParens ? '(' : '';
      serializeNode(node.left);
    }

    // node is NOT
    if (node.child) {
      output += stringify(node.token) + '(';
      serializeNode(node.child);
      output += ')';
    } else if (node.token.type !== TOKEN_TYPE.NOT) {
      const sp = node.token.parseType === PARSE_TYPE.TERM ? '' : ' '; // terms handle their own spacing
      output += node.type === TOKEN_TYPE.AND && omitAnd ? sp : sp + stringify(node.token) + sp;
    }

    // node is AND or OR
    if (node.right) {
      serializeNode(node.right);
      output += needParens ? ')' : '';
    }
  };

  serializeNode(ast);

  // strip surrounding parens
  if (output[0] === '(' && output[output.length - 1] === ')') {
    output = output.substring(1, output.length - 1);
  }

  return output;
};

/**
 * Returns true if the given filter represents a Power Query, because it has a command in it.
 *
 * @param filter    a filter
 *
 * @return true if the given filter represents a Power Query
 */
export const isPowerQuery = (filter: string): boolean => {
  if (!filter) {
    return false;
  }

  const tokens = tokenizeFilter(filter);
  return tokens.length > 0 && tokens.findIndex((token) => token.type === TOKEN_TYPE.PIPE) !== -1;
};

/**
 * Returns true if the given filter represents a "complex" expression, which is a function
 * that resolves to a numeric value, used in graphing.
 *
 * @param filter    a filter
 *
 * @return true if the given filter represents a complex expression
 */
export const isComplexExpression = (filter: string): boolean => {
  // functions can appear in a PQ and fool us (should possibly be parsed as PQ funcs)
  if (!filter || isPowerQuery(filter)) {
    return false;
  }

  const tokens = tokenizeFilter(filter);
  return tokens.length > 0 && tokens.findIndex((token) => token.type === TOKEN_TYPE.FUNCTION) !== -1;
};

/**
 * Returns true if the given filter represents a constant graphing expression, for example: 1 * 8
 * It is used to draw a line with a particular value (8 in the example above).
 *
 * @param filter    a filter
 *
 * @return true if the given filter represents a constant graphing expression
 */
export const isConstantExpression = (filter: string): boolean => {
  const tokens = tokenizeFilter(filter);
  return (
    tokens.length === 3 &&
    tokens[0].type === TOKEN_TYPE.NUMBER &&
    tokens[2].type === TOKEN_TYPE.NUMBER &&
    tokens[1].text === K.MATH_OPERATOR.MULTIPLY
  );
};

/**
 * Returns true if the given filter represents two or more graphing functions joined by math operators, eg:
 *   count(message contains "error") / count(message contains "success")
 *
 * @param filter    a filter
 * @returns true if the filter is a complex math expression
 */
export const isComplexMathExpression = (filter: string): boolean => {
  const expTypes = findExpressions(tokenizeFilter(filter))
    .map((exp) => exp.type)
    .join(' ');
  return /^function( math-operator function)+$/.test(expTypes);
};
